<template>
    <transition name="fade-transition">
        <div
            v-if="ui.socialShare.opened"
            class="fixed inset-0 z-999 flex h-full w-full items-center justify-center p-4"
            @click.stop="closeSocialShareClickingOutside($event)"
        >
            <div
                ref="focusTrapContainer"
                role="dialog"
                aria-modal="true"
                :aria-label="shareTitle"
                class="relative w-full max-w-[400px] overflow-hidden rounded-lg border border-theme-border bg-theme-bg text-theme-text shadow-lg"
                :class="{ fixPositionInAudiobooks: fixPositionInAudiobooks }"
            >
                <div class="flex items-center justify-between px-3 py-3">
                    <h4 class="m-0">{{ shareTitle }}</h4>
                    <button
                        class="d-flex px-2 focus-visible:ring-2 focus-visible:ring-theme-accent focus-visible:ring-offset-2"
                        type="button"
                        :aria-label="localizations[lang].tooltips.close"
                        @click="closeSocialShareBox"
                    >
                        <svg-icon icon="outline-clear-24px"></svg-icon>
                    </button>
                </div>
                <transition name="fade" mode="out-in">
                    <WeChatShare
                        v-if="showWeChatShare"
                        :url-for-share="issueUrlForShare"
                        @close="showWeChatShare = false"
                    />
                    <div v-else class="py-2">
                        <div
                            class="flex cursor-pointer items-center px-4 py-3 hover:bg-theme-icon-bg"
                            @click="shareIn('facebook')"
                        >
                            <svg-icon icon="facebook-icon-24px" width="24px" height="24px"></svg-icon>
                            <span class="ml-3 text-theme-text">Facebook</span>
                        </div>
                        <div
                            class="flex cursor-pointer items-center px-4 py-3 hover:bg-theme-icon-bg"
                            @click="shareIn('x')"
                        >
                            <svg-icon icon="x-icon-24px" width="24px" height="24px"></svg-icon>
                            <span class="ml-3 text-theme-text">x.com</span>
                        </div>
                        <div
                            class="flex cursor-pointer items-center px-4 py-3 hover:bg-theme-icon-bg"
                            @click="shareIn('whatsapp')"
                        >
                            <svg-icon icon="whatsapp-icon-24px" width="24px" height="24px"></svg-icon>
                            <span class="ml-3 text-theme-text">WhatsApp</span>
                        </div>
                        <div
                            class="flex cursor-pointer items-center px-4 py-3 hover:bg-theme-icon-bg"
                            @click="showWeChatShare = !showWeChatShare"
                        >
                            <svg-icon icon="WeChat_24" width="24px" height="24px"></svg-icon>
                            <span class="ml-3 text-theme-text">WeChat</span>
                        </div>
                        <div
                            class="flex cursor-pointer items-center px-4 py-3 hover:bg-theme-icon-bg"
                            @click="shareUrlOnly"
                        >
                            <svg-icon
                                icon="baseline-link-24px"
                                class="text-theme-toolbar-icon"
                                width="24px"
                                height="24px"
                            ></svg-icon>
                            <span class="ml-3 text-theme-text">{{ localizations[lang].copyUrl }}</span>
                        </div>
                    </div>
                </transition>
                <div
                    v-if="!ui.socialShare.shareArticle"
                    class="flex items-center border-t border-theme-border px-4 py-3"
                >
                    <div class="relative flex items-center">
                        <input
                            id="shareWithLocation"
                            v-model="shareWithLocation"
                            type="checkbox"
                            name="shareWithLocation"
                            class="peer h-5 w-5 cursor-pointer appearance-none rounded border border-theme-button-border checked:border-theme-accent checked:bg-theme-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-accent"
                        />
                        <svg-icon
                            icon="check"
                            width="20px"
                            height="20px"
                            class="pointer-events-none absolute left-0 hidden text-theme-accent-on peer-checked:flex"
                        />
                        <label
                            for="shareWithLocation"
                            class="my-auto ml-2 flex cursor-pointer select-none items-center text-theme-text"
                        >
                            <span class="align-middle">{{ shareWithLocationText }}</span>
                        </label>
                    </div>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
import SocialNeworksLinksTemplates from '../../js/utils/SocialNeworksLinksTemplates.json';
import WeChatShare from './WeChatShare.vue';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import focusTrapMixin from '../../js/mixins/focusTrapMixin';
import { addOnLink } from '../../js/utils/addOnLink';

export default {
    components: {
        WeChatShare,
    },
    mixins: [focusTrapMixin],
    data() {
        return {
            shareWithLocation: true,
            showWeChatShare: false,
            focusTrapEnabled: false,
        };
    },
    computed: {
        ...mapState(['issue', 'ui', 'title', 'lastLocation', 'fileType', 'device', 'localizations', 'lang']),
        ...mapGetters(['isPdf', 'currentPage', 'isAudiobook', 'onlyMobileVisible']),
        hasShareApi() {
            return navigator.share;
        },
        issueUrlForShare() {
            // Only the reader answers a position; the publication page drops it.
            if (this.ui.socialShare.shareArticle) {
                return addOnLink(this.issue.issue_url, this.ui.socialShare.currentArticleId);
            }

            if (this.shareWithLocation) {
                const location = this.isPdf ? this.currentPage : btoa(JSON.stringify(this.lastLocation[this.fileType]));
                return `${this.issue.issue_url}?location=${location}`;
            }

            return this.issue.share_url || this.issue.issue_url;
        },
        fixPositionInAudiobooks() {
            return this.isAudiobook && this.device.width > 1420;
        },
        shareTitle() {
            return this.ui.socialShare.shareArticle
                ? this.localizations[this.lang].shareArticle
                : this.localizations[this.lang].shareIssue;
        },
        shareWithLocationText() {
            return this.isAudiobook
                ? this.localizations[this.lang].shareCurrentPosition
                : this.localizations[this.lang].shareCurrentPage;
        },
    },
    watch: {
        'ui.socialShare.opened': {
            immediate: true,
            handler(isOpen) {
                this.focusTrapEnabled = isOpen;
            },
        },
    },
    methods: {
        ...mapMutations(['openToast', 'closeSocialShare']),
        ...mapActions(['trackEvent', 'buildUrlAndGoToSocialNetworksSharing', 'copyToClipBoard']),
        onFocusTrapEscape() {
            this.closeSocialShareBox();
        },
        shareIn(network) {
            const url = SocialNeworksLinksTemplates.publication[network];
            const title = this.truncateLongText(this.title, 100);
            const authors = Object.prototype.hasOwnProperty.call(this.issue.tags, 'author')
                ? this.issue.tags.author.join(', ')
                : null;

            this.buildUrlAndGoToSocialNetworksSharing({
                url,
                network,
                title,
                authors,
                readerUrl: this.issueUrlForShare,
            });

            this.closeSocialShareBox();
        },
        truncateLongText(text, length = 140) {
            if (text.length > length) {
                return `${text.substring(0, length)}...`;
            }

            return text;
        },
        async shareUrlOnly() {
            this.trackEvent({
                action: 'share',
                issue_id: this.issue.id,
                trigger: 'click',
                source: 'volpe:copy-url',
            });
            const wasCopied = await this.copyToClipBoard(this.issueUrlForShare);
            if (!wasCopied) return;

            this.closeSocialShare();
            this.openToast({ text: this.localizations[this.lang].copyUrlToClipboard });
        },
        closeSocialShareClickingOutside(event) {
            if (this.$el === event.target) {
                this.closeSocialShareBox();
            }
        },
        closeSocialShareBox() {
            this.showWeChatShare = false;
            this.closeSocialShare();
        },
    },
};
</script>
